import type { AppChoiceGroup } from "../hooks/useIntegrationsCatalog";
import { isAppGroupToken } from "../hooks/useIntegrationsCatalog";
import type { Integration } from "../types/integration";

// One thing an admin pinned to the customer's invite, resolved against what
// this tenant has actually connected.
//
// A requirement is either a single app ("connect ServiceM8") or a CHOICE
// between several ("connect your email" — Gmail or Outlook or ...), because
// we often don't know which product a customer uses until the call. A choice
// is satisfied by any one of its options.
export type RequiredApp = {
  // The pinned token: a catalog slug, or a "group:" choice token.
  token: string;
  title: string;
  // What the customer can click. One entry for a single app; several for a
  // choice. Empty means nothing is currently connectable for this requirement.
  options: ReadonlyArray<{ slug: string; title: string; integration: Integration }>;
  // Set only for a choice, to prompt the customer ("Connect your email").
  prompt: string | null;
  connected: boolean;
  // Whether the customer can do anything about it themselves. False when
  // nothing behind the requirement is connectable for them (not configured,
  // admin-gated, retired). Those still show — the admin asked for them — but
  // they never block the onboarding call, because no amount of clicking would
  // unblock it.
  actionable: boolean;
};

const isConnected = (integration: Integration): boolean =>
  integration.connectionStatus === "active" ||
  (integration.connections ?? []).some((c) => c.status === "active");

// Pinned tokens, in the admin's order, joined to the tenant's live integration
// rows. A token we can no longer resolve is dropped rather than rendered as a
// dead row (same rule the onboarding cards use).
export const buildRequiredApps = (
  tokens: ReadonlyArray<string>,
  integrations: ReadonlyArray<Integration>,
  groups: ReadonlyArray<AppChoiceGroup> = [],
): ReadonlyArray<RequiredApp> => {
  const bySlug = new Map(integrations.map((i) => [i.slug, i]));
  const groupById = new Map(groups.map((g) => [g.id, g]));

  return tokens.flatMap((token): RequiredApp[] => {
    if (isAppGroupToken(token)) {
      const group = groupById.get(token);
      if (group === undefined) return [];
      const options = group.members
        .map((m) => bySlug.get(m.slug))
        .filter((i): i is Integration => i !== undefined)
        .map((i) => ({ slug: i.slug, title: i.title, integration: i }));
      // Nothing servable behind the group — don't render an empty prompt.
      if (options.length === 0) return [];
      return [{
        token,
        title: group.title,
        options,
        prompt: group.prompt,
        connected: options.some((o) => isConnected(o.integration)),
        actionable: options.some((o) => o.integration.configured),
      }];
    }

    const integration = bySlug.get(token);
    if (integration === undefined) return [];
    return [{
      token,
      title: integration.title,
      options: [{ slug: integration.slug, title: integration.title, integration }],
      prompt: null,
      connected: isConnected(integration),
      actionable: integration.configured,
    }];
  });
};

// The onboarding call is gated until every requirement the customer CAN action
// is satisfied. Requirements they can't action are excluded on purpose:
// pinning something the catalog won't serve must not lock a customer out of
// talking to us.
export const outstandingApps = (
  apps: ReadonlyArray<RequiredApp>,
): ReadonlyArray<RequiredApp> => apps.filter((a) => a.actionable && !a.connected);

// `resolved` says whether `apps` is the answer or just the empty list we start
// from: until the integrations and groups behind it have loaded, an empty
// `apps` is indistinguishable from "nothing was pinned", and reading it as the
// latter hands over the very CTA this gate protects. Unresolved therefore
// holds the gate closed — callers pass `true` only once they know.
// A load that FAILED still counts as resolved: a customer stuck behind a
// permanently disabled button is worse than a missed nudge, the same
// never-strand rule that excludes non-actionable apps above.
export const bookingBlocked = (
  apps: ReadonlyArray<RequiredApp>,
  resolved: boolean,
): boolean => !resolved || outstandingApps(apps).length > 0;
